// ---------------------------------------------------------------------------
// OperatorView — SP-5f Task C3 (restyle dashboard-kpi)
// Card grid Velzon (Card reactstrap + avatar) per ogni sessione operatore.
// ---------------------------------------------------------------------------

import { Link } from 'react-router-dom';
import { Card, CardBody, Progress } from 'reactstrap';
import { StatusBadge } from '../StatusBadge';
import type { Session } from '../../hooks/liveReducer';
import type { LiveOperatorDto } from '../../api/live';
import { fmtRelative } from './relativeTime';

const FSM_LABEL: Record<string, string> = {
  SAY_LOCATION: 'Conferma location',
  WAIT_LOCATION_CONFIRM: 'Conferma location',
  // V2: l'ubicazione non ha piu' un turno operatore; il passo automatico fa
  // parte dell'annuncio dell'item.
  CONFIRMING_LOCATION: 'Conferma item',
  SAY_ITEM_NAME: 'Conferma item',
  WAIT_DISCRIMINATOR_CONFIRM: 'Conferma item',
  // digits-fallback: cifre non capite dopo i tentativi → inserimento manuale.
  WAIT_DISCRIMINATOR_MANUAL: 'Cifre manuali',
  SAY_QTY: 'Conferma quantita',
  WAIT_QTY_CONFIRM: 'Conferma quantita',
  WAIT_QTY_MANUAL: 'Quantita manuale',
  SAY_FORCE_PROMPT: 'Conferma parziale',
  PAUSED: 'In pausa',
};

const TONE_BG: Record<string, string> = {
  success: 'bg-success-subtle text-success',
  warn:    'bg-warning-subtle text-warning',
  info:    'bg-info-subtle text-info',
  neutral: 'bg-secondary-subtle text-secondary',
};

function initials(name: string): string {
  const parts = name.trim().split(/[\s.@_-]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export interface OperatorViewProps {
  sessions: Session[];
  /**
   * Roster completo degli operatori del magazzino (anche non collegati). Gli
   * operatori presenti nel roster ma SENZA sessione attiva sono mostrati come
   * card "offline". Opzionale (default []) per retro-compatibilità.
   */
  roster?: LiveOperatorDto[];
}

export function OperatorView({ sessions, roster = [] }: OperatorViewProps) {
  // SP-8c — tre gruppi di card:
  //  1) sessioni ONLINE (operatore collegato, dati real-time);
  //  2) sessioni OFFLINE (operatore disconnesso: card con l'ultimo avanzamento
  //     noto + timestamp, NON si azzera);
  //  3) operatori del roster SENZA alcuna sessione (offline, nessun avanzamento).
  const sessionOpIds = new Set(sessions.map((s) => s.operatorUserId));
  const rosterOffline = roster.filter((op) => !sessionOpIds.has(op.user_id));
  // Conteggio "missioni in coda" per ogni operatore (dal roster, che include
  // anche gli online): le card di sessione lo leggono via user_id.
  const remainingByUser = new Map<number, number>(
    roster.map((o) => [o.user_id, o.assigned_missions_remaining ?? 0]),
  );
  // Online prima, poi le sessioni offline-con-avanzamento.
  const orderedSessions = [...sessions].sort(
    (a, b) => Number(a.offline ?? false) - Number(b.offline ?? false),
  );

  if (sessions.length === 0 && rosterOffline.length === 0) {
    return <p className="text-muted mb-0">Nessun operatore configurato per questo magazzino.</p>;
  }
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        gap: 12,
      }}
    >
      {orderedSessions.map((s) => {
        const isOffline = s.offline === true;
        const statusTone: 'success' | 'warn' | 'info' | 'neutral' =
          isOffline ? 'neutral' : s.isMuted ? 'info' : s.isPaused ? 'warn' : 'success';
        const statusLabel = isOffline ? 'offline' : s.isMuted ? 'muted' : s.isPaused ? 'paused' : 'attivo';
        const fsmLabel = s.fsmState ? (FSM_LABEL[s.fsmState] ?? s.fsmState) : '—';
        const pct = s.tasksTotal && s.tasksTotal > 0 && s.tasksDone !== null
          ? Math.round((s.tasksDone / s.tasksTotal) * 100)
          : null;

        return (
          <Card key={s.sessionId} className="card-animate mb-0 h-100"
                style={isOffline ? { opacity: 0.7 } : undefined}>
            <CardBody className="p-3">
              <div className="d-flex align-items-center mb-2">
                <div className="avatar-sm flex-shrink-0 me-2">
                  <span className={`avatar-title rounded-circle fs-13 fw-semibold ${TONE_BG[statusTone]}`}>
                    {initials(s.operatorDisplayName)}
                  </span>
                </div>
                <div className="flex-grow-1 overflow-hidden">
                  <h6 className="mb-0 fs-14 text-truncate" title={s.operatorDisplayName}>
                    {s.operatorDisplayName}
                  </h6>
                  <small className="text-muted">
                    {s.pickListExternalId && s.pickListId
                      ? <Link to={`/pick-lists/${s.pickListId}`} className="font-monospace">{s.pickListExternalId}</Link>
                      : <span className="font-monospace">{s.pickListExternalId ?? '—'}</span>}
                    {s.pickListRecipient ? ` · ${s.pickListRecipient}` : ''}
                  </small>
                </div>
                <StatusBadge tone={statusTone}>{statusLabel}</StatusBadge>
              </div>

              <div className="d-flex flex-wrap gap-3 small text-muted mb-2">
                {s.currentTaskLocation
                  ? <span><i className="bi bi-geo-alt me-1" />{s.currentTaskLocation}</span>
                  : null}
                {s.currentTaskItemName
                  ? <span className="text-truncate" style={{ maxWidth: 140 }} title={s.currentTaskItemName}>
                      <i className="bi bi-box-seam me-1" />{s.currentTaskItemName}
                    </span>
                  : null}
                {!s.currentTaskLocation && !s.currentTaskItemName
                  ? <span className="fst-italic">nessun task</span>
                  : null}
              </div>

              {s.qtyRequired !== null && (
                <div className="d-flex justify-content-between small mb-2">
                  <span className="text-muted">Riga corrente</span>
                  <span className="fw-medium">{s.qtyPicked ?? 0}/{s.qtyRequired}</span>
                </div>
              )}

              {pct !== null && (
                <div>
                  <div className="d-flex justify-content-between small text-muted mb-1">
                    <span>Incarico: {s.tasksDone}/{s.tasksTotal} prelievi</span>
                    <span>{pct}%</span>
                  </div>
                  <Progress
                    value={pct}
                    color="success"
                    style={{ height: 4 }}
                    aria-label={`Progresso incarico ${pct}%`}
                  />
                  {s.tasksTotal !== null && s.tasksDone !== null && s.tasksTotal - s.tasksDone > 0 && (
                    <div className="small text-muted mt-1">{s.tasksTotal - s.tasksDone} prelievi da fare</div>
                  )}
                </div>
              )}

              <div className="small text-muted mt-2">
                <i className="bi bi-mic me-1" />{fsmLabel}
              </div>

              {(() => {
                const seen = fmtRelative(s.lastEventAt);
                return (
                  <div className="small text-muted mt-1" title={seen.title}>
                    <i className="bi bi-clock-history me-1" />Ultimo agg. {seen.label}
                  </div>
                );
              })()}
              {(remainingByUser.get(s.operatorUserId) ?? 0) > 0 && (
                <div className="small text-muted mt-1">
                  <i className="bi bi-list-task me-1" />{remainingByUser.get(s.operatorUserId)} missioni in coda
                </div>
              )}
            </CardBody>
          </Card>
        );
      })}

      {rosterOffline.map((op) => {
        // SP-8c — avanzamento persistito (DB) della mission assegnata, mostrato
        // anche se il dashboard non ha assistito alla sessione live.
        const pct = op.tasks_total && op.tasks_total > 0 && op.tasks_done != null
          ? Math.round((op.tasks_done / op.tasks_total) * 100)
          : null;
        return (
          <Card key={`offline-${op.user_id}`} className="mb-0 h-100" style={{ opacity: 0.85 }}>
            <CardBody className="p-3">
              <div className="d-flex align-items-center mb-2">
                <div className="avatar-sm flex-shrink-0 me-2">
                  <span className="avatar-title rounded-circle fs-13 fw-semibold bg-secondary-subtle text-secondary">
                    {initials(op.display_name)}
                  </span>
                </div>
                <div className="flex-grow-1 overflow-hidden">
                  <h6 className="mb-0 fs-14 text-truncate" title={op.display_name}>
                    {op.display_name}
                  </h6>
                  <small className="text-muted">
                    {op.pick_list_external_id && op.pick_list_id
                      ? <Link to={`/pick-lists/${op.pick_list_id}`} className="font-monospace">{op.pick_list_external_id}</Link>
                      : op.current_mission_id !== null
                        ? `Mission #${op.current_mission_id} assegnata`
                        : 'Nessuna mission assegnata'}
                  </small>
                </div>
                <StatusBadge tone="neutral">offline</StatusBadge>
              </div>

              {op.qty_assigned_total != null && op.qty_assigned_total > 0 && (
                <div className="d-flex justify-content-between small mb-2">
                  <span className="text-muted">Quantità</span>
                  <span className="fw-medium">{op.qty_picked_total ?? 0}/{op.qty_assigned_total}</span>
                </div>
              )}

              {pct !== null && (
                <div className="mb-2">
                  <div className="d-flex justify-content-between small text-muted mb-1">
                    <span>Incarico: {op.tasks_done}/{op.tasks_total} prelievi</span>
                    <span>{pct}%</span>
                  </div>
                  <Progress value={pct} color="secondary" style={{ height: 4 }}
                            aria-label={`Progresso incarico ${pct}%`} />
                  {op.tasks_total != null && op.tasks_done != null && op.tasks_total - op.tasks_done > 0 && (
                    <div className="small text-muted mt-1">{op.tasks_total - op.tasks_done} prelievi da fare</div>
                  )}
                </div>
              )}

              <div className="small text-muted">
                <i className="bi bi-plug me-1" />Non collegato
              </div>
              {(() => {
                const seen = fmtRelative(op.last_seen_at ?? null);
                return (
                  <div className="small text-muted mt-1" title={seen.title}>
                    <i className="bi bi-clock-history me-1" />Ultimo agg. {seen.label}
                  </div>
                );
              })()}
              {(op.assigned_missions_remaining ?? 0) > 0 && (
                <div className="small text-muted mt-1">
                  <i className="bi bi-list-task me-1" />{op.assigned_missions_remaining} missioni in coda
                </div>
              )}
            </CardBody>
          </Card>
        );
      })}
    </div>
  );
}
